import { type PetEquipment } from './petEquipmentService.ts';
import type { PetCompanion } from '../domain/pet.ts';
import { petMaxHealthSlots } from '../domain/pet.ts';
import { applyDamageToHealthBar, healSlots, type HealthBar, type HealthBarDamageResult } from '../rules/health.ts';
import { statModForScore } from '../rules/statModifiers.ts';

/**
 * Pet Health Bars use the same slot threshold rule as the ordinary Health Bar:
 * each slot is worth the creature's Con Mod. Pets differ only in how many
 * slots they have (Size at Level 1, +1/level, max 10) and in dying instantly
 * at 0% rather than receiving the crawler Dying countdown.
 */
export function petHealthBar(pet: Pick<PetCompanion, 'unenhancedStats' | 'size' | 'level' | 'healthBarSlotsRemaining'>, equipment?: PetEquipment): HealthBar {
  return {
    slotValue: statModForScore(equipment?.enhancedStats.con ?? pet.unenhancedStats.con),
    maxSlots: petMaxHealthSlots(pet),
    slotsRemaining: pet.healthBarSlotsRemaining,
  };
}

export interface PetDamageOutcome {
  pet: PetCompanion;
  healthBar: HealthBarDamageResult;
}

/** Damage supplied here is already post-DR/resistance, matching the crawler sheet's manual Damage control. */
export function takePetPostMitigationDamage(pet: PetCompanion, damage: number, now: string, equipment?: PetEquipment): PetDamageOutcome {
  const healthBar = applyDamageToHealthBar(petHealthBar(pet, equipment), damage);
  return {
    pet: { ...pet, healthBarSlotsRemaining: healthBar.bar.slotsRemaining, updatedAt: now },
    healthBar,
  };
}

export function healPetHealthSlots(pet: PetCompanion, slots: number, now: string): PetCompanion {
  const bar = healSlots(petHealthBar(pet), slots);
  return { ...pet, healthBarSlotsRemaining: bar.slotsRemaining, updatedAt: now };
}
